import { conversationSearchPattern, conversationSearchWords } from '@upright/common/chat/conversation-search';
import { Button, IconButton } from '@upright/ui/common/generic/components/button';
import { Skeleton, SkeletonProvider } from '@upright/ui/common/generic/components/skeleton';
import { StateMessage } from '@upright/ui/common/generic/components/state-message';
import { Text } from '@upright/ui/common/generic/components/typography';
import { TableV2 } from '@upright/ui/common/generic/content/tables/table-v2';
import type { ColumnV2 } from '@upright/ui/common/generic/content/tables/table-v2/types';
import { RecycleBin2StreamlineCore } from '@upright/ui/icons';
import { chatConversationPath } from '@upright/ui/views/unified-shell/unified-shell-chat-routes';
import { differenceInCalendarDays, format } from 'date-fns';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChatDeleteDialog } from './chat-delete-dialog';
import {
  type ChatConversationListItem,
  conversationDisplayTitle,
  useChatConversationPagesQuery,
} from './chat-history-data';
import styles from './chats-list.module.css';

/**
 * Every conversation newest first, a page at a time, or the ones matching `query` (PLT-7658). Each
 * row is the conversation's title and when it was last active; a search result adds the line it
 * matched in, with the matched words in semibold. The Chats page shows it, and so does the chat
 * side panel's Chats view (PLT-7883).
 *
 * Its own list rather than the `/chat` sidebar's `ChatHistoryList`: the sidebar is a 260px column
 * of titles beside a conversation, and this list's rows carry a date and a matched line. The two
 * still read the same server list and delete through the same dialog and mutation, so a
 * conversation deleted here is gone from the sidebar and the rail too.
 *
 * A row is a link to the conversation's own URL. `onOpenConversation` is for a surface that opens
 * the conversation where it already is, the side panel: it takes a plain click, and a modified
 * click still opens the link in a new tab. `currentConversationId` marks the conversation that
 * surface is showing.
 */
export function ChatsList({
  query,
  onClearSearch,
  onOpenConversation,
  currentConversationId = null,
  layout = 'list',
}: {
  query: string;
  onClearSearch: () => void;
  onOpenConversation?: (id: string) => void;
  currentConversationId?: string | null;
  /**
   * `table` is the Chats page's (`ChatsTable`); the side panel keeps the list, which fits its
   * column and marks the conversation it is showing.
   */
  layout?: 'list' | 'table';
}) {
  const pagesQuery = useChatConversationPagesQuery(query);
  const [pendingDelete, setPendingDelete] = useState<ChatConversationListItem | null>(null);
  // Memoised on the query, so the table's columns are not rebuilt on every render.
  const words = useMemo(() => conversationSearchWords(query), [query]);
  const isSearch = query !== '';

  // Rows already loaded stay up when only a later page fails; Show more is still there to retry it.
  const data = pagesQuery.data;
  let content: ReactNode;
  if (pagesQuery.isPending) {
    content = <LoadingRows />;
  } else if (data === undefined) {
    content = (
      <StateMessage
        layout="section"
        title={isSearch ? 'Search isn’t working right now' : 'Couldn’t load your chats'}
        description={
          isSearch
            ? 'Your chats are all still here. Try again in a moment, or clear the search to see the whole list.'
            : 'Try again in a moment.'
        }
        secondaryAction={
          isSearch ? (
            <Button variant="outlined" onPress={onClearSearch}>
              Clear search
            </Button>
          ) : undefined
        }
        primaryAction={
          <Button
            variant="outlined"
            onPress={() => {
              void pagesQuery.refetch();
            }}
            isPending={pagesQuery.isRefetching}
          >
            Try again
          </Button>
        }
        announce
      />
    );
  } else {
    const items = data.pages.flatMap(page => page.items);
    const total = data.pages.at(0)?.total ?? items.length;
    if (items.length === 0) {
      content = isSearch ? (
        <StateMessage
          layout="section"
          title={`No chats match “${query}”`}
          description="Search looks in chat titles and in everything you and Upright AI wrote, and every word has to match. Try fewer words."
          primaryAction={
            <Button variant="outlined" onPress={onClearSearch}>
              Clear search
            </Button>
          }
          announce
        />
      ) : (
        <StateMessage layout="section" title="No chats yet" description="Chats you start appear here, newest first." />
      );
    } else {
      content = (
        <>
          {/* A live region, so a screen reader hears how many chats the words left, as a sighted reader sees. */}
          <div role="status" className={styles.count}>
            {isSearch && (
              <Text size="sm" height="xs" color="gray-600">
                {total === 1 ? '1 chat' : `${total} chats`}
              </Text>
            )}
          </div>
          {layout === 'table' ? (
            <ChatsTable items={items} words={words} onRequestDelete={setPendingDelete} />
          ) : (
            <ul className={styles.rows}>
              {items.map(item => (
                <ChatsListRow
                  key={item.id}
                  item={item}
                  words={words}
                  isCurrent={item.id === currentConversationId}
                  onOpen={onOpenConversation}
                  onRequestDelete={setPendingDelete}
                />
              ))}
            </ul>
          )}
          {pagesQuery.hasNextPage && (
            <div className={styles.more}>
              <Button
                variant="outlined"
                onPress={() => {
                  void pagesQuery.fetchNextPage();
                }}
                isPending={pagesQuery.isFetchingNextPage}
              >
                Show more
              </Button>
            </div>
          )}
        </>
      );
    }
  }

  return (
    <>
      {content}
      {/* Outside the branches above, so deleting the last row does not unmount the dialog mid-delete. */}
      {pendingDelete !== null && (
        <ChatDeleteDialog
          conversation={pendingDelete}
          onClose={() => {
            setPendingDelete(null);
          }}
        />
      )}
    </>
  );
}

/**
 * One conversation: a link to it, holding the title, the matched line on a search, and when it was
 * last active; then the delete button, outside the link because a button cannot sit inside one. The
 * delete shows on hover and on keyboard focus, as the sidebar row's does.
 *
 * The current conversation is the lilac "where you are" pill with its text in the same hue and the
 * title in medium, so the selection has a second cue besides the fill (design/50-components.md
 * "A state treatment is not an emphasis treatment").
 */
function ChatsListRow({
  item,
  words,
  isCurrent,
  onOpen,
  onRequestDelete,
}: {
  item: ChatConversationListItem;
  words: string[];
  isCurrent: boolean;
  onOpen: ((id: string) => void) | undefined;
  onRequestDelete: (item: ChatConversationListItem) => void;
}) {
  const title = conversationDisplayTitle(item);
  return (
    <li className={isCurrent ? `${styles.row} ${styles.current}` : styles.row}>
      <Link
        to={chatConversationPath(item.id)}
        className={styles.link}
        title={title}
        aria-current={isCurrent ? 'true' : undefined}
        onClick={
          onOpen === undefined
            ? undefined
            : event => {
                // Modifier and non-primary clicks stay with the link, which opens a new tab.
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                event.preventDefault();
                onOpen(item.id);
              }
        }
      >
        <span className={styles.main}>
          <Text
            size="md"
            height="sm"
            color={isCurrent ? 'lilac-800' : 'gray-700'}
            weight={isCurrent ? 'medium' : undefined}
            truncate
          >
            <MatchedWords text={title} words={words} />
          </Text>
          {typeof item.matchSnippet === 'string' && (
            <Text size="sm" height="xs" color={isCurrent ? 'lilac-800' : 'gray-600'} truncate>
              <MatchedWords text={item.matchSnippet} words={words} />
            </Text>
          )}
        </span>
        <Text size="sm" height="xs" color={isCurrent ? 'lilac-800' : 'gray-600'} whiteSpace="nowrap">
          {lastActiveLabel(item.updatedAt)}
        </Text>
      </Link>
      <span className={styles.delete}>
        <IconButton
          variant="text"
          size="small"
          icon={RecycleBin2StreamlineCore}
          aria-label={`Delete “${title}”`}
          onPress={() => {
            onRequestDelete(item);
          }}
        />
      </span>
    </li>
  );
}

/**
 * The Chats page's conversations as a light table, the plain `TableV2` (PLT-8068, after the Eevo
 * console's Chats page): Chat and Last active, under a header row, a hairline between rows, the
 * date right-aligned under its heading. A chat's cell is its title in regular `gray-900`, with the
 * matched line under it on a search; the title is the row's link, so the keyboard and a modified
 * click work as on any link, and a click anywhere else on the row opens the chat too, with a
 * `gray-50` fill on the hovered row. The delete button is the last cell, shown on the row's hover
 * and keyboard focus as the list row's; its press does not reach the row, because React Aria stops
 * the click it handles.
 */
function ChatsTable({
  items,
  words,
  onRequestDelete,
}: {
  items: ChatConversationListItem[];
  words: string[];
  onRequestDelete: (item: ChatConversationListItem) => void;
}) {
  const navigate = useNavigate();
  const columns = useMemo<Array<ColumnV2<ChatConversationListItem>>>(
    () => [
      {
        id: 'chat',
        header: 'Chat',
        meta: { width: 'minmax(12rem, 1fr)' },
        cell: ({ row }) => {
          const title = conversationDisplayTitle(row.original);
          return (
            <Link
              to={chatConversationPath(row.original.id)}
              className={styles.tableLink}
              title={title}
              onClick={event => {
                // The link opens the chat itself, also in a new tab on a modified click.
                event.stopPropagation();
              }}
            >
              <Text size="md" height="sm" color="gray-900" truncate>
                <MatchedWords text={title} words={words} />
              </Text>
              {typeof row.original.matchSnippet === 'string' && (
                <Text size="sm" height="xs" color="gray-600" truncate>
                  <MatchedWords text={row.original.matchSnippet} words={words} />
                </Text>
              )}
            </Link>
          );
        },
      },
      {
        id: 'lastActive',
        header: 'Last active',
        meta: { width: '10rem', align: 'right' },
        cell: ({ row }) => (
          <Text size="md" height="sm" color="gray-600" whiteSpace="nowrap">
            {lastActiveLabel(row.original.updatedAt)}
          </Text>
        ),
      },
      {
        id: 'delete',
        header: '',
        meta: { width: '3.5rem', align: 'right' },
        cell: ({ row }) => (
          <span className={styles.tableDelete}>
            <IconButton
              variant="text"
              size="small"
              icon={RecycleBin2StreamlineCore}
              aria-label={`Delete “${conversationDisplayTitle(row.original)}”`}
              onPress={() => {
                onRequestDelete(row.original);
              }}
            />
          </span>
        ),
      },
    ],
    [words, onRequestDelete],
  );
  return (
    <div className={styles.table}>
      <TableV2
        columns={columns}
        data={items}
        variant="plain"
        pageSize={false}
        disableSort
        onRowClick={row => {
          navigate(chatConversationPath(row.original.id));
        }}
      />
    </div>
  );
}

/** The matched words in semibold `gray-900`: weight and ink, not a fill, which would read as a state. */
function MatchedWords({ text, words }: { text: string; words: string[] }) {
  if (words.length === 0) {
    return <>{text}</>;
  }
  const parts: ReactNode[] = [];
  let end = 0;
  for (const match of text.matchAll(conversationSearchPattern(words, 'g'))) {
    if (match.index > end) {
      parts.push(text.slice(end, match.index));
    }
    parts.push(
      <mark key={match.index} className={styles.match}>
        {match[0]}
      </mark>,
    );
    end = match.index + match[0].length;
  }
  parts.push(text.slice(end));
  return <>{parts}</>;
}

function LoadingRows() {
  return (
    <SkeletonProvider isLoading>
      <div className={styles.loading} aria-busy="true" aria-label="Loading chats">
        {['first', 'second', 'third', 'fourth'].map(row => (
          <Skeleton key={row} minBlockSize="2.25rem">
            <div>&nbsp;</div>
          </Skeleton>
        ))}
      </div>
    </SkeletonProvider>
  );
}

/**
 * When the conversation was last active: relative for the past week, then the customer-facing date
 * pattern (design/20-numbers-and-charts.md "Numbers, units and dates").
 */
function lastActiveLabel(updatedAt: string): string {
  const date = new Date(updatedAt);
  const days = differenceInCalendarDays(new Date(), date);
  if (days <= 0) {
    return 'Today';
  }
  if (days === 1) {
    return 'Yesterday';
  }
  if (days < 7) {
    return `${days} days ago`;
  }
  return format(date, 'MMM do, y');
}
